import { useEffect, useRef, useState } from 'react'
import { ClientAnalyzer } from '../ClientAnalyzer'
import { LevelMeterPro } from '../LevelMeterPro'
import { DeviceSelect } from './DeviceSelect'
import { Panel } from './Panel'
import { RadioGroupPanel } from './RadioGroupPanel'
import { SlpButton } from './SlpButton'
import { StatusBar } from './StatusBar'
import { StatusLamp } from './StatusLamp'
import '../../styles/slp.css'

/**
 * /gallery — dev-only showcase of the SLP component library (U1 deliverable).
 * Renders every component in its main states so U2 (room layout) and the
 * Playwright checks have a stable surface. Not linked from any user flow.
 */

const BITRATE_OPTIONS = [
  { value: '64-mono', label: '64 kbit/s mono' },
  { value: '128-mono', label: '128 kbit/s mono' },
  { value: '192-mono', label: '192 kbit/s mono' },
  { value: '256-mono', label: '256 kbit/s mono' },
  { value: '192-stereo', label: '192 kbit/s stereo' },
  { value: '128-lr', label: '128 kbit/s L/R split' },
]

/** Bouncing demo level (dBFS) so the horizontal meters visibly move. */
function useDemoSignal(periodMs: number, minDb: number, maxDb: number): number {
  const [db, setDb] = useState(minDb)
  useEffect(() => {
    const t0 = performance.now()
    const iv = window.setInterval(() => {
      const ph = ((performance.now() - t0) % periodMs) / periodMs
      const s = (Math.sin(ph * Math.PI * 2) + 1) / 2 // 0..1
      // Add a small fast flutter so the LED edge looks alive.
      const flutter = Math.sin(ph * Math.PI * 26) * 1.5
      setDb(minDb + (maxDb - minDb) * s + flutter)
    }, 50)
    return () => window.clearInterval(iv)
  }, [periodMs, minDb, maxDb])
  return db
}

/** 0 dBFS burst for the first 600 ms, then a steady -20 — latches CLIP deterministically. */
function useClipBurst(): number {
  const [db, setDb] = useState(0)
  useEffect(() => {
    const t = window.setTimeout(() => setDb(-20), 600)
    return () => window.clearTimeout(t)
  }, [])
  return db
}

export function SlpGallery() {
  const send = useDemoSignal(2600, -48, -7)
  const receive = useDemoSignal(3400, -42, -12)
  const clipDemo = useClipBurst()
  const [bitrate, setBitrate] = useState('256-mono')
  const [clipCount, setClipCount] = useState(0)
  const clipCountRef = useRef(0)

  return (
    <div
      data-testid="slp-gallery"
      className="flex min-h-screen flex-col bg-[var(--slp-page)] text-[var(--slp-text)]"
    >
      <div className="mx-auto w-full max-w-5xl flex-1 px-4 py-5">
        <h1 className="mb-1 text-[17px] font-semibold">SLP Component Gallery (dev)</h1>
        <p className="mb-4 text-[12px] text-[var(--slp-text-dim)]">
          rt UI v2 — SessionLinkPRO-style light component library. Dev-only route, not part of any
          user flow.
        </p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Buttons */}
          <Panel title="Buttons">
            <div className="flex flex-wrap items-center gap-2">
              <SlpButton variant="primary">Create Session Link</SlpButton>
              <SlpButton variant="secondary">Mute Input</SlpButton>
              <SlpButton variant="danger">Disconnect</SlpButton>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <SlpButton size="sm">Connect</SlpButton>
              <SlpButton size="sm" variant="secondary">
                chat
              </SlpButton>
              <SlpButton size="sm" variant="danger" disabled>
                Disconnect
              </SlpButton>
            </div>
          </Panel>

          {/* Status lamps */}
          <Panel title="Status Lamps">
            <div className="flex flex-col gap-1.5 text-[13px]">
              {(
                [
                  ['ok', 'online / ON AIR'],
                  ['warn', 'warning'],
                  ['error', 'error'],
                  ['off', 'offline'],
                ] as const
              ).map(([status, text]) => (
                <div key={status} className="flex items-center gap-2">
                  <StatusLamp status={status} />
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </Panel>

          {/* Channel setup radio group */}
          <Panel title="Channel Setup: Actor">
            <RadioGroupPanel
              name="bitrate-actor"
              legend="Receive settings"
              options={BITRATE_OPTIONS}
              value={bitrate}
              onChange={setBitrate}
            />
          </Panel>

          {/* Device selects */}
          <Panel title="Audio / MIDI Devices">
            <div className="flex flex-col gap-2">
              <DeviceSelect kind="audioinput" />
              <DeviceSelect kind="audiooutput" />
              <DeviceSelect kind="videoinput" label="Video Input" />
            </div>
          </Panel>

          {/* Horizontal meters — SLP Audio Levels (wide, like the SLP panel) */}
          <Panel title="Audio Levels" className="md:col-span-2">
            <div className="flex flex-col gap-3">
              <LevelMeterPro id="send" label="Send" peakDbfs={send} />
              <LevelMeterPro id="receive" label="Receive" peakDbfs={receive} />
              <div className="flex justify-end">
                <SlpButton size="sm">Mute Input</SlpButton>
              </div>
            </div>
          </Panel>

          {/* Vertical big meters */}
          <Panel title="Record Meter (vertical)" className="md:row-span-2">
            <div className="flex items-start justify-center gap-6 pt-1">
              <LevelMeterPro
                id="vu-vertical"
                variant="vertical"
                label="Input"
                peakDbfs={clipDemo}
                height={260}
                onClip={() => {
                  clipCountRef.current += 1
                  setClipCount(clipCountRef.current)
                }}
              />
              <LevelMeterPro
                id="vu-static"
                variant="vertical"
                label="Static -12"
                peakDbfs={-12}
                height={260}
              />
            </div>
            <p className="mt-2 text-center text-[11px] text-[var(--slp-text-dim)]">
              clip events: <span data-testid="clip-count">{clipCount}</span> · click any meter body
              to reset peak hold + CLIP
            </p>
          </Panel>

          {/* Client analyzer — healthy / degraded / no data */}
          <ClientAnalyzer
            title="Client Analyzer (ok)"
            subject="Remote User 1"
            metrics={{
              rttMs: 32,
              packetLossPct: 0.1,
              jitterMs: 4.2,
              bitrateKbps: 254,
              diskFreeMb: 51_200,
            }}
            targetBitrateKbps={256}
          />
          <ClientAnalyzer
            title="Client Analyzer (degraded)"
            subject="Remote User 2"
            metrics={{
              rttMs: 310,
              packetLossPct: 1.8,
              jitterMs: 71,
              bitrateKbps: 96,
              diskFreeMb: 900,
            }}
            targetBitrateKbps={256}
          />
          <ClientAnalyzer
            title="Client Analyzer (no data)"
            subject="Remote User 3 (offline)"
            metrics={{}}
          />
        </div>
      </div>

      <StatusBar event="Connection established to voice talent 2" role="studio" />
    </div>
  )
}
